/**
 * The content model, described for the platform's editor shell.
 *
 * The shell renders from this rather than anything hard-coded, which is how one
 * editor bundle serves every project including ones with custom block types.
 *
 * Add a project block here and in `blocks/`, and the admin will offer it.
 */

import { coreBlockTypes } from '@remidus/web-core'

const LABELS: Record<string, string> = {
  summary: 'Summary',
  heading: 'Heading',
  paragraph: 'Paragraph',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  model: '3D model',
  gif: 'GIF',
  code: 'Code',
  embed: 'Embed',
  gallery: 'Gallery',
  faq: 'FAQ',
  divider: 'Divider',
}

export default {
  blocks: [
    ...coreBlockTypes.map((type) => ({ type, label: LABELS[type] ?? type })),

    // This project's own four. Each is server-rendered by
    // api/app/prerender.py, so the editor can offer it as a real type with
    // real fields rather than as a paragraph carrying a variant.
    //
    // A spec that declares `fields` keeps them; one that does not inherits
    // core's for that type. These declare theirs.
    {
      type: 'bubble',
      label: 'Bubble pull-out',
      fields: [
        { key: 'kicker', label: 'Kicker', type: 'text' },
        { key: 'heading', label: 'Heading', type: 'text' },
        {
          key: 'text',
          label: 'Text',
          type: 'textarea',
          help: 'Blank line between paragraphs.',
        },
        {
          key: 'checklist',
          label: 'Checklist',
          type: 'list',
          help: 'Ticked points under the text. Use it for a short set of things that had to be true, not as a substitute for a sentence.',
          item: [{ key: 'text', label: 'Point', type: 'text' }],
        },
      ],
    },
    {
      type: 'timeline',
      label: 'Timeline',
      fields: [
        {
          key: 'items',
          label: 'Steps',
          type: 'list',
          help: 'The build in the order it happened. Each step is a card; one with a picture splits into two columns.',
          item: [
            { key: 'date', label: 'Date', type: 'text' },
            { key: 'year', label: 'Year', type: 'text' },
            { key: 'kicker', label: 'Kicker', type: 'text' },
            { key: 'title', label: 'Title', type: 'text' },
            { key: 'text', label: 'Text', type: 'textarea' },
            { key: 'media_id', label: 'Picture or clip', type: 'media' },
            {
              key: 'media_ids',
              label: 'Pictures and clips',
              type: 'media-list',
              help: 'Two or more turns the card into a grid. Takes anything the media library holds: stills, video, GIFs. Overrides the single picture above.',
            },
            {
              key: 'specs',
              label: 'Print settings',
              type: 'list',
              help: 'Shown as a labelled table under the text. A row left blank still shows, as a dash, so an unrecorded setting reads as unrecorded rather than as absent.',
              item: [
                { key: 'label', label: 'Setting', type: 'text' },
                { key: 'value', label: 'Value', type: 'text' },
              ],
            },
            { key: 'flip', label: 'Picture on the right', type: 'boolean' },
          ],
        },
      ],
    },
    {
      type: 'viewer',
      label: 'CAD viewer',
      fields: [
        { key: 'caption', label: 'Caption', type: 'text' },
        {
          key: 'parts',
          label: 'Part list',
          type: 'list',
          help: 'Shown beside the model, and it is the whole block for a reader with no WebGL.',
          item: [
            { key: 'name', label: 'Part', type: 'text' },
            { key: 'detail', label: 'Detail', type: 'text' },
          ],
        },
      ],
    },
  ],

  pages: {
    singletons: ['home'],
    collections: [],
  },

  // Project-level settings the admin should offer under Settings.
  settings: [
    // { key: 'heroModel', type: 'media', kinds: ['model'] },
  ],

  seo: true,
  analytics: true,
  social: true,
}
